import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Database, Save } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import AdminLayout, { AdminPageHeader } from "./layout";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

interface RetentionSetting {
  defaultRetentionDays: 30 | 90 | 180;
  allowedValues: ReadonlyArray<30 | 90 | 180>;
}

export default function AdminRetentionSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string>("");
  const [confirming, setConfirming] = useState(false);

  const { data, isLoading } = useQuery<RetentionSetting>({
    queryKey: ["admin", "settings", "retention"],
    queryFn: async () => {
      const res = await fetch(`${basePath}/api/admin/settings/retention`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load retention setting");
      return res.json() as Promise<RetentionSetting>;
    },
  });

  useEffect(() => {
    if (data && !selected) setSelected(String(data.defaultRetentionDays));
  }, [data, selected]);

  const mutation = useMutation({
    mutationFn: async (defaultRetentionDays: number) => {
      const res = await fetch(`${basePath}/api/admin/settings/retention`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ defaultRetentionDays }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error ?? "Failed to update retention setting");
      }
      return res.json() as Promise<RetentionSetting>;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(["admin", "settings", "retention"], updated);
      queryClient.invalidateQueries({ queryKey: ["admin", "audit-log"] });
      setConfirming(false);
      toast({
        title: "Default retention period updated",
        description: `New default is ${updated.defaultRetentionDays} days. Takes effect on the next retention sweep.`,
      });
    },
    onError: (err: Error) => {
      setConfirming(false);
      toast({
        title: "Update failed",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const current = data?.defaultRetentionDays;
  const allowed = data?.allowedValues ?? ([30, 90, 180] as const);
  const selectedNum = Number(selected);
  const isDirty = current !== undefined && selectedNum !== current;

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Data Retention"
        description="Global default retention period applied to patient chat history when a patient has not set their own preference."
      />

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="h-4 w-4 text-teal-700" />
            Default retention period
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading || current === undefined ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : (
            <>
              <div className="text-sm text-slate-600">
                Current default:{" "}
                <span className="font-semibold text-slate-900">
                  {current} days
                </span>
              </div>

              <div className="flex items-end gap-3">
                <div className="flex-1 max-w-xs">
                  <label
                    htmlFor="retention-days"
                    className="block text-xs font-medium text-slate-600 mb-1"
                  >
                    New default
                  </label>
                  <Select
                    value={selected}
                    onValueChange={(v) => setSelected(v)}
                  >
                    <SelectTrigger id="retention-days">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {allowed.map((d) => (
                        <SelectItem key={d} value={String(d)}>
                          {d} days
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {!confirming ? (
                  <Button
                    onClick={() => setConfirming(true)}
                    disabled={!isDirty}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setConfirming(false)}
                      disabled={mutation.isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => mutation.mutate(selectedNum)}
                      disabled={mutation.isPending}
                    >
                      {mutation.isPending ? "Saving…" : "Confirm change"}
                    </Button>
                  </div>
                )}
              </div>

              {confirming && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  This will change the default retention period from{" "}
                  <strong>{current} days</strong> to{" "}
                  <strong>{selectedNum} days</strong>. It applies to patients
                  who have not chosen their own retention preference. Patient
                  chat history may be deleted sooner at the next daily
                  retention sweep (03:00 UTC) if you choose a shorter period.
                </div>
              )}

              <p className="text-xs text-slate-500">
                Patients can override this default in their own privacy
                settings. The retention sweep runs daily at 03:00 UTC.
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
